import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { toPng } from "html-to-image";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
// Direct imports (not the kit index) so the offline HTML report runtime can reuse this chart.
import ChartCard from "../m3/ChartCard";
import M3Button from "../m3/M3Button";
import M3IconButton from "../m3/M3IconButton";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import { getOrgTokens } from "../../theme/style/organizationPalette";

const FLOW = GRAPH_PALETTE.circularityFlows;

const formatPercent = (value) => Number(value.toFixed(1)).toString();

/**
 * Sums the share of a side that is flagged as circular (e.g. everything except virgin / waste).
 */
const getCircularShare = (values, series) =>
  series.reduce((sum, s) => (s.circular ? sum + (values[s.key] || 0) : sum), 0);

/**
 * Single horizontal 100% bar built from flex segments sized proportionally to their value.
 */
const FlowBar = ({ values, series, isDark, t }) => (
  <Box sx={{ display: "flex", gap: FLOW.segmentGap, height: FLOW.barHeight, width: "100%" }}>
    {series
      .filter((s) => (values[s.key] || 0) > 0)
      .map((s) => {
        const value = values[s.key];
        const color = FLOW[s.flow];
        return (
          <Tooltip key={s.key} title={`${t(s.label)}: ${formatPercent(value)}%`} arrow>
            <Box
              sx={{
                flex: `${value} 1 0`,
                minWidth: "6px",
                backgroundColor: isDark ? color.dark : color.light,
                color: color.text,
                borderRadius: FLOW.segmentRadius,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                fontFamily: GRAPH_PALETTE.font.family,
                fontSize: GRAPH_PALETTE.font.sizes.standard,
                fontWeight: GRAPH_PALETTE.font.weights.medium,
                cursor: "default",
              }}
            >
              {value >= FLOW.minLabelPercent ? formatPercent(value) : ""}
            </Box>
          </Tooltip>
        );
      })}
  </Box>
);

/**
 * Material flow chart: one row per material showing where it comes from (input bar)
 * and where it goes at end of life (output bar), joined by an arrow.
 *
 * Series config: [{ key, label, flow, circular }] where `flow` is a key of
 * GRAPH_PALETTE.circularityFlows and `circular` marks the share counted as circular.
 */
const MaterialFlowChart = ({
  title,
  rows = [],
  inputSeries = [],
  outputSeries = [],
  inputTitle = "Where material comes from",
  outputTitle = "Where it goes at end of life",
  maxRows = 15,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const ref = useRef(null);
  const [showAll, setShowAll] = useState(false);

  const handleDownload = () => {
    if (!ref.current) return;
    toPng(ref.current, { cacheBust: true, backgroundColor: tok.bg.card })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${t(title || "Material Flows")}.png`;
        link.click();
      })
      .catch((error) => console.error("Error generating image:", error));
  };

  const visibleRows = showAll ? rows : rows.slice(0, maxRows);

  // Legend lists each flow type once, in input-then-output order.
  const legendItems = useMemo(() => {
    const seen = new Set();
    return [...inputSeries, ...outputSeries].filter((s) => {
      if (seen.has(s.flow)) return false;
      seen.add(s.flow);
      return true;
    });
  }, [inputSeries, outputSeries]);

  const textSx = {
    fontFamily: tok.font.family,
    fontSize: tok.font.size.sectionTitle,
    color: tok.text.primary,
  };
  const circularSx = {
    fontFamily: tok.font.family,
    fontSize: tok.font.size.bodyText,
    color: tok.text.secondary,
    whiteSpace: "nowrap",
  };
  const columnHeaderSx = {
    fontFamily: tok.font.family,
    fontSize: tok.font.size.sectionTitle,
    fontWeight: tok.font.weight.semibold,
    color: tok.text.primary,
    textAlign: "left",
  };
  const gridColumns = { xs: "1fr", md: "1fr 56px 1fr" };

  return (
    <ChartCard
      title={title ? t(title) : undefined}
      padded={false}
      actions={
        <>
          {rows.length > maxRows && (
            <M3Button size="sm" variant="tonal" onClick={() => setShowAll((prev) => !prev)}>
              {showAll ? t("Show Top {{count}}", { count: maxRows }) : t("Show All")}
            </M3Button>
          )}
          <M3IconButton
            variant="standard"
            size="sm"
            icon={<FileDownloadOutlinedIcon />}
            tooltip={t("Download")}
            onClick={handleDownload}
          />
        </>
      }
    >
      <Box ref={ref} sx={{ backgroundColor: tok.bg.card, display: "flex", flexDirection: "column" }}>
        <Box sx={{ p: tok.spacing.chartPad, display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Legend */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {legendItems.map((s) => (
              <Box key={s.flow} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: tok.radius.iconSm,
                    backgroundColor: isDark ? FLOW[s.flow].dark : FLOW[s.flow].light,
                  }}
                />
                <Typography sx={circularSx}>{t(s.legendLabel || s.label)}</Typography>
              </Box>
            ))}
          </Box>

          {/* Column headers */}
          <Box sx={{ display: "grid", gridTemplateColumns: gridColumns, columnGap: 2 }}>
            <Typography sx={columnHeaderSx}>{t(inputTitle)}</Typography>
            <Box sx={{ display: { xs: "none", md: "block" } }} />
            <Typography sx={{ ...columnHeaderSx, display: { xs: "none", md: "block" } }}>{t(outputTitle)}</Typography>
          </Box>

          {visibleRows.length === 0 && (
            <Typography sx={{ ...circularSx, textAlign: "left" }}>{t("No materials available")}</Typography>
          )}

          {visibleRows.map((row, idx) => {
            const inputCircular = getCircularShare(row.input, inputSeries);
            const outputCircular = getCircularShare(row.output, outputSeries);
            return (
              <Box
                key={row.id ?? idx}
                sx={{ display: "grid", gridTemplateColumns: gridColumns, columnGap: 2, rowGap: 1, alignItems: "end" }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Tooltip
                    arrow
                    title={t("Circular share: {{input}}% in, {{output}}% out", {
                      input: formatPercent(inputCircular),
                      output: formatPercent(outputCircular),
                    })}
                  >
                    <Typography sx={{ ...textSx, mb: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "default" }}>
                      {row.label}
                    </Typography>
                  </Tooltip>
                  <FlowBar values={row.input} series={inputSeries} isDark={isDark} t={t} />
                </Box>

                <Box
                  sx={{
                    height: FLOW.barHeight,
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    justifyContent: "center",
                    color: isDark ? FLOW.arrow.dark : FLOW.arrow.light,
                  }}
                >
                  <EastRoundedIcon />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <FlowBar values={row.output} series={outputSeries} isDark={isDark} t={t} />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </ChartCard>
  );
};

export default MaterialFlowChart;
